import cn from 'classnames';
import * as React from 'react';
import { useRef } from 'react';
import { throttle } from 'throttle-debounce';

import { SeoLink } from './SeoLink';
import { SeoLinksMoreButton } from './SeoLinksMoreButton';
import * as style from './style.css';
import { getLinksLength, LINKS_GAP, MORE_BUTTON_LENGTH } from './utils/linkLength';
import { ISeoTabLink } from '../../../types/seoTabsLinks';

interface IShortListProps {
  links: ISeoTabLink[];
  isExpanded: boolean;
  onMoreButtonClick(): void;
}

export const SeoLinksList = ({ links, isExpanded, onMoreButtonClick }: IShortListProps) => {
  const ref: React.RefObject<HTMLDivElement | null> = useRef(null);

  const [linksWidth, setLinksWidth] = React.useState(0);
  const [linksCount, setLinksCount] = React.useState(links.length);

  /* istanbul ignore next */
  const listStyle = React.useMemo(() => {
    return isExpanded
      ? {}
      : { width: linksWidth ? `${linksWidth}px` : `calc(100% - ${MORE_BUTTON_LENGTH}px - ${LINKS_GAP}px)` };
  }, [isExpanded, linksWidth]);

  React.useLayoutEffect(() => {
    function onResize() {
      const width = ref.current?.getBoundingClientRect().width;

      /* istanbul ignore next */
      if (width) {
        const [linksLength, count] = getLinksLength(width, links);
        setLinksWidth(linksLength);
        setLinksCount(count);
      }
    }

    const throttledCallback = throttle(200, onResize);

    if (isExpanded) {
      window.removeEventListener('resize', throttledCallback);
      setLinksCount(links.length);

      return;
    }

    onResize();
    window.addEventListener('resize', throttledCallback);

    return () => {
      window.removeEventListener('resize', throttledCallback);
    };
  }, [links, isExpanded]);

  return (
    <div className={style['links-block']} ref={ref}>
      <div className={cn(style['links'], isExpanded && style['links--expanded'])} style={listStyle}>
        {links.slice(0, linksCount).map((link, index) => (
          <SeoLink link={link} key={index} />
        ))}
        <SeoLinksMoreButton isExpanded={isExpanded} onClick={onMoreButtonClick} />
        {links.slice(linksCount).map((link, index) => (
          <SeoLink link={link} key={`rest_${index}`} />
        ))}
      </div>
    </div>
  );
};
